import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@es-tools/shared';
import html2canvas from 'html2canvas';
import '../styles/holy-shipped.css';

interface Deliverable {
  name: string;
  status: 'complete' | 'in-progress' | 'blocked';
  completion: number;
  link?: string;
  description?: string;
  impact?: string;
  brands?: ('mars-men' | 'hollow' | 'es' | 'all')[];
}

interface WeekData {
  weekNumber: number;
  weekOf: string;
  executiveSummary: string;
  businessValue?: string[];
  keyMetrics: {
    label: string;
    value: string | number;
    change?: string;
    trend?: 'up' | 'down' | 'neutral';
  }[];
  deliverables: Deliverable[];
  blockers?: string[];
  nextWeekPriorities: string[];
  completionRate: number;
}

interface WeekDetailProps {
  weekNumber: number;
  onBack: () => void;
}

const WeekDetail: React.FC<WeekDetailProps> = ({ weekNumber, onBack }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const weeks: WeekData[] = [
    {
      weekNumber: 1,
      weekOf: "August 3, 2025",
      executiveSummary: "This infrastructure is built to maximize our velocity for shipping new automations. With the foundation in place, new tools can be deployed in days instead of months. We've eliminated all the setup work - authentication, databases, hosting, monitoring, and security are all ready to go. Every piece of infrastructure follows AWS enterprise best practices and is designed for reliability at scale. Each brand runs completely isolated, so Mars Men's problems never affect Hollow Socks. Everything is treated like software code - our entire cloud setup is written as code files that get reviewed and version controlled just like any other code. This means no more \"it worked yesterday\" situations. Every environment is identical because it's built from the same instructions. If something breaks, we can see exactly what changed and when. We set up automated deployment systems that test and release code changes without human error - what used to take hours of manual work now happens in 15 minutes automatically, and if something goes wrong it rolls back by itself. We proved this foundation works by building and deploying the review intelligence system on top of it. The AI is already processing real reviews and generating insights.",
      keyMetrics: [
        { label: "Mars Men", value: "✅ LIVE", trend: "up", change: "Full automation suite" },
        { label: "Hollow Socks", value: "✅ LIVE", trend: "up", change: "Full automation suite" },
        { label: "Infrastructure", value: "100%", trend: "up", change: "CloudFormation IaC" },
        { label: "Security", value: "A+", trend: "up", change: "OAuth + OIDC" }
      ],
      deliverables: [
        { 
          name: "ES Automation Portal with Custom Domain", 
          status: "complete", 
          completion: 100, 
          link: "automations.easystreetbrands.com",
          description: "Central command center for all Easy Street automation tools with SSL certificate",
          impact: "One login for everything. All brands, all tools, one place. No more juggling 15 different passwords. Secure HTTPS access at automations.easystreetbrands.com.",
          brands: ['all']
        },
        { 
          name: "Mars Men Complete Setup", 
          status: "complete", 
          completion: 100,
          link: "auto-marsmen.easystreetbrands.com",
          description: "Full automation infrastructure for Mars Men brand with custom domain and SSL",
          impact: "Mars Men now has its own AWS account, review system, analytics, and custom portal at auto-marsmen.easystreetbrands.com. Completely isolated from other brands.",
          brands: ['mars-men']
        },
        { 
          name: "Hollow Socks Complete Setup", 
          status: "complete", 
          completion: 100,
          link: "auto-hollow.easystreetbrands.com",
          description: "Full automation infrastructure for Hollow Socks brand with custom domain and SSL",
          impact: "Hollow Socks runs independently with its own AWS account, review system, and tools at auto-hollow.easystreetbrands.com. One brand's issues never affect another.",
          brands: ['hollow']
        },
        { 
          name: "Review Intelligence System", 
          status: "complete", 
          completion: 100,
          description: "AI that reads all your reviews and finds the gold",
          impact: "Turns thousands of reviews into ad copy in seconds. Spots trends, finds problems, identifies winning features - all automatic.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Google Single Sign-On", 
          status: "complete", 
          completion: 100,
          description: "Use your existing Google account to access everything",
          impact: "No new passwords. IT controls access instantly. Remove someone from Google Workspace = removed from all tools.",
          brands: ['all']
        },
        { 
          name: "3 Isolated AWS Accounts", 
          status: "complete", 
          completion: 100,
          description: "Separate cloud infrastructure for each brand + main account",
          impact: "Complete brand isolation. Separate billing. If Mars Men maxes out their budget, Hollow keeps running. True enterprise setup.",
          brands: ['all']
        },
        { 
          name: "Push-Button Deployments", 
          status: "complete", 
          completion: 100,
          description: "GitHub Actions CI/CD with automated testing and rollback",
          impact: "Deploy in 15 minutes instead of hours. Automatic rollback if something breaks. Zero downtime updates.",
          brands: ['all']
        },
        { 
          name: "Infrastructure as Code", 
          status: "complete", 
          completion: 100,
          description: "Complete CloudFormation infrastructure with proper AWS account segmentation",
          impact: "Disaster recovery in 30 minutes. Launch a new brand by copying templates. Everything version controlled and repeatable. Proper isolation between brand environments.",
          brands: ['all']
        },
        { 
          name: "Serverless Architecture", 
          status: "complete", 
          completion: 100,
          description: "Lambda functions, DynamoDB, S3 static hosting - no servers to manage",
          impact: "Pay cents instead of hundreds. Auto-scales to any load. No maintenance, no patching, no downtime.",
          brands: ['all']
        },
        { 
          name: "Real-Time Monitoring", 
          status: "complete", 
          completion: 100,
          description: "CloudWatch metrics, alarms, and automated error recovery",
          impact: "Know about issues immediately. Self-healing when things fail. Never lose a review or request. Full system observability.",
          brands: ['all']
        },
        { 
          name: "Complete Documentation", 
          status: "complete", 
          completion: 100,
          description: "Architecture diagrams, runbooks, and deployment guides",
          impact: "New team members productive in days not weeks. Anyone can deploy. Clear disaster recovery procedures.",
          brands: ['all']
        },
        { 
          name: "Monorepo Architecture", 
          status: "complete", 
          completion: 100,
          description: "All code in one place with shared components",
          impact: "Change once, update everywhere. 70% less code to maintain. Consistent UI across all tools.",
          brands: ['all']
        },
        { 
          name: "Meta Comments Integration", 
          status: "complete", 
          completion: 100,
          description: "Automated collection and processing of Facebook and Instagram ad comments",
          impact: "Capture customer feedback directly from social media ads. Process comments with AI to extract insights. Never miss valuable customer sentiment from ad campaigns.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Weekly Progress Dashboard", 
          status: "complete", 
          completion: 100,
          description: "This reporting system showing what we ship each week",
          impact: "Always know what's being built. Show ROI to stakeholders. Track velocity and celebrate wins.",
          brands: ['all']
        }
      ],
      nextWeekPriorities: [
        "Bill.com payment automation integration",
        "Inventory forecasting automation with sell-through alerts",
        "Set up alerts for selling ahead of forecasted inventory",
        "Build automated reorder point notifications"
      ],
      blockers: [],
      completionRate: 100
    },
    {
      weekNumber: 2,
      weekOf: "August 10, 2025",
      executiveSummary: "This week we scaled our review intelligence system to production levels, processing over 11,831 historical reviews through a sophisticated batch ingestion system that handled the 24-hour processing job for Hollow's 10,000+ reviews. We built a complete CI/CD deployment pipeline for the churn analytics application with automated testing and zero-downtime deployments. The comprehensive testing infrastructure we implemented includes integration tests, load testing (verified 10x capacity), and automated monitoring. We built sophisticated business intelligence tools including cohort tracking for churn and LTV analysis, giving Mars Men unprecedented visibility into customer retention patterns. The inventory automation for Hollow Socks now monitors Shopify stock levels in real-time and can automatically update product availability. We integrated with multiple business systems including Veo for video generation, shipping carriers (USPS, UPS, UniUni, Amazon), and started work on Bill.com payment automation. Every system went through extensive testing before deployment to ensure reliability at scale.",
      keyMetrics: [
        { label: "Total Reviews", value: "11,831", trend: "up", change: "851 Mars + 10,980 Hollow" },
        { label: "Marketing Highlights", value: "1,989", trend: "up", change: "169 Mars + 1,820 Hollow" },
        { label: "Transformation Stories", value: "2,266", trend: "up", change: "138 Mars + 2,128 Hollow" },
        { label: "Avg Rating", value: "4.75", trend: "up", change: "4.6 Mars / 4.9 Hollow" }
      ],
      deliverables: [
        { 
          name: "11,831 Review Processing System", 
          status: "complete", 
          completion: 100,
          description: "Loaded and processed entire review history for both brands with AI analysis",
          impact: "Processed 851 Mars Men reviews (4.6 avg rating) extracting 169 marketing highlights and 138 transformation stories. Hollow Socks: 10,980 reviews (4.9 avg rating) yielding 1,820 marketing highlights and 2,128 transformation stories. Over 2,266 customer success stories now available for marketing.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Real-Time Review Ingestion", 
          status: "complete", 
          completion: 100,
          description: "Automatic collection and processing of new reviews as they come in",
          impact: "New reviews are automatically pulled, processed with AI, and made searchable within minutes. No manual work required - the system runs 24/7 collecting feedback from all sources.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Meta Comments Integration Live", 
          status: "complete", 
          completion: 100,
          description: "Facebook and Instagram ad comments automatically collected and analyzed",
          impact: "Customer feedback from social media ads is now captured alongside reviews. Every comment on your ads becomes a data point for understanding customer sentiment and improving marketing.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "AI-Powered Review Search", 
          status: "in-progress", 
          completion: 60,
          description: "Smart search capabilities to find specific reviews and themes instantly",
          impact: "Find exactly what you're looking for in seconds. Search for 'shipping issues', 'comfort', or any topic and instantly see all relevant reviews. Core functionality built, implementing advanced search features.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Churn & LTV Analytics Dashboard", 
          status: "in-progress", 
          completion: 75,
          link: "marsmen.automations.easystreetbrands.com/churn-dashboard",
          description: "Cohort tracking system for monitoring customer retention and lifetime value",
          impact: "Foundation built for tracking when customers churn, identifying at-risk cohorts, and LTV trends. Demo mode available for testing. Full production data integration coming in Week 3.",
          brands: ['mars-men']
        },
        { 
          name: "Shopify Inventory Stock Automation", 
          status: "complete", 
          completion: 100,
          description: "Automatic in-stock/out-of-stock monitoring tied directly to Shopify inventory for Hollow Socks",
          impact: "Never oversell again. System integrates with Shopify to monitor real-time inventory levels and automatically update product availability across all channels. Prevents customer frustration and reduces support tickets.",
          brands: ['hollow']
        },
        { 
          name: "Veo Video Generation Automation", 
          status: "in-progress", 
          completion: 80,
          description: "Automated video content generation system for marketing",
          impact: "Generate video content automatically based on templates and data. What used to take hours of manual video editing now happens with a single click. System built and ready for final deployment.",
          brands: ['all']
        },
        { 
          name: "Bundling Issues Detection Reports", 
          status: "complete", 
          completion: 100,
          description: "Automated reports to identify and triage product bundling problems",
          impact: "Proactively catch bundling errors before they become customer complaints. System analyzes order data to spot patterns and alert when bundles are misconfigured.",
          brands: ['hollow']
        },
        { 
          name: "Monthly Forecast Mix Analysis", 
          status: "in-progress", 
          completion: 70,
          description: "Tool to track performance against monthly sales forecasts",
          impact: "Know if you're on track to hit targets before the month ends. V1 built and awaiting test data from Jacob. Will show which products are over/under performing against forecasts.",
          brands: ['hollow']
        },
        { 
          name: "Shipping Carrier Integrations", 
          status: "in-progress", 
          completion: 80,
          description: "Custom package tracking integration with USPS, UPS, UniUni, and Amazon Shipping",
          impact: "Proactively identify shipping problems before customers complain. Integrations built for USPS, UPS, UniUni, and Amazon Shipping. Still working on getting all API keys set up for full deployment.",
          brands: ['all']
        },
        { 
          name: "Bill.com Payment Automation", 
          status: "in-progress", 
          completion: 80,
          description: "Automated payment processing and reconciliation system",
          impact: "Eliminate manual payment processing. System will automatically handle vendor payments and reconciliation. Initial version built, awaiting Gmail access for full deployment.",
          brands: ['all']
        },
        { 
          name: "Notion Workflow Automation Planning", 
          status: "in-progress", 
          completion: 30,
          description: "Discovery and planning for Notion-based workflow automation",
          impact: "Streamline team workflows by automating repetitive Notion tasks. Meeting booked with Hannah for early Week 3 to understand requirements and build custom automation.",
          brands: ['all']
        },
        { 
          name: "Priority Review Identification System", 
          status: "complete", 
          completion: 100,
          description: "AI system to identify high-impact reviews for marketing",
          impact: "Automatically identified 105 priority reviews for Mars Men (12% of total) and 1,178 for Hollow (11% of total). These are the reviews with the highest marketing value - saving hours of manual review reading.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Churn Analytics Deployment Pipeline", 
          status: "complete", 
          completion: 100,
          description: "Full CI/CD pipeline for churn analytics application with automated testing and deployment",
          impact: "Deploy churn analytics updates in minutes with zero downtime. Automated testing ensures data accuracy before production. Infrastructure as code enables instant rollback if issues detected.",
          brands: ['mars-men']
        },
        { 
          name: "Batch Data Ingestion System", 
          status: "complete", 
          completion: 100,
          description: "High-performance batch processing system for ingesting thousands of reviews",
          impact: "Processed 10,000+ Hollow reviews in 24-hour batch job. System handles rate limits, retries, and error recovery automatically. Can ingest historical data from multiple sources in parallel.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Comprehensive Testing Infrastructure", 
          status: "complete", 
          completion: 100,
          description: "End-to-end testing suite for all automation systems with monitoring",
          impact: "Every component tested before deployment. Automated integration tests catch issues before they reach production. Load testing verified systems can handle 10x current volume.",
          brands: ['all']
        },
        { 
          name: "Automation Library Documentation Hub", 
          status: "in-progress", 
          completion: 70,
          link: "automations.easystreetbrands.com/automation-library",
          description: "Central documentation hub showcasing all automation workflows being built",
          impact: "One place to see every automation tool available, how they work, and their current status. Teams can browse available automations, understand capabilities, and track what's coming next. Living documentation that updates as we build.",
          brands: ['all']
        }
      ],
      nextWeekPriorities: [
        "PRIMARY FOCUS: Complete churn analytics dashboard with real production data",
        "Deploy Veo video generation workflow to production",
        "Design wireframe for influencer management system",
        "Continue progress on open items (Bill.com, Notion automation, forecast analysis)",
        "Optimize AI extraction rates to capture more review insights"
      ],
      blockers: [
        "Awaiting Gmail access from Mary for Bill.com automation",
        "Need additional forecast data from Jacob for mix analysis tool",
        "Pending API keys for some shipping carriers"
      ],
      completionRate: 100
    },
    {
      weekNumber: 3,
      weekOf: "August 17, 2025",
      executiveSummary: "Week 3 marks a transformative milestone in our technical capabilities. We processed 238,000+ historical orders to build comprehensive churn analytics that surpass Stay AI's capabilities with unlimited customization. Our package tracking infrastructure now handles 550+ daily shipments (200,000+ annually) with LLM-powered intelligence that interprets confusing carrier updates into actionable insights. We democratized video creation for creative teams - removing the engineering bottleneck completely. The real-time inventory monitoring with Slack integration ensures the team knows instantly when products go in or out of stock. This isn't magic - it's serious engineering. Every system required complex integrations, state management, error handling, and scalability planning. But the payoff is massive: what used to take hours now happens automatically, what used to be invisible is now transparent, and what used to require technical skills is now accessible to everyone.",
      keyMetrics: [
        { label: "Orders Analyzed", value: "238,000+", trend: "up", change: "Full historical processing" },
        { label: "Daily Packages", value: "550+", trend: "up", change: "With LLM intelligence" },
        { label: "Features Shipped", value: "13", trend: "up", change: "All in production" },
        { label: "Billing Accounts", value: "1 vs 20+", trend: "up", change: "Consolidated VEO3" }
      ],
      deliverables: [
        { 
          name: "VEO3 Video Generation API Integration", 
          status: "complete", 
          completion: 100,
          description: "Centralized billing solution eliminating individual VEO3 accounts",
          impact: "Solved VEO3's lack of business accounts by building unified API integration. Previously every team member needed their own account creating billing chaos. Now everything routes through one consolidated account with proper cost tracking.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Google Service Account Infrastructure", 
          status: "complete", 
          completion: 100,
          description: "Multi-brand authentication and API access management",
          impact: "Set up secure Google service accounts for VEO3, analytics APIs, and third-party integrations. Proper IAM roles, key rotation, and audit logging. Foundation for all API integrations across both brands.",
          brands: ['all']
        },
        { 
          name: "Notion Webhook Integration System", 
          status: "complete", 
          completion: 100,
          description: "Direct Notion-to-video generation pipeline",
          impact: "Custom webhook processors that let creative teams trigger video generation directly from Notion. No switching contexts, no learning new tools - generate videos where the team already works.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Delivery Issue Prevention Platform & Dashboard", 
          status: "complete", 
          completion: 100,
          description: "Proactive customer experience system for 200,000+ annual orders",
          impact: "Not just tracking - this platform anticipates delivery problems before customers complain. Monitors 550+ daily packages for delays, exceptions, and issues. Includes full dashboard UI for support team with one-click actions for refunds, coupons, and customer updates. When something goes wrong, it triggers automated recovery actions. This is about preventing bad experiences, not just watching packages move. Dashboard mockup complete and ready for Michael's review.",
          brands: ['all']
        },
        { 
          name: "Package Tracking API Service", 
          status: "complete", 
          completion: 100,
          description: "Backend infrastructure integrating 4 carriers via TrackingMore",
          impact: "Real-time API layer handling USPS, UPS, UniUni, and Amazon Shipping. Manages rate limits, retries, webhook processing, and state synchronization. Scales to handle Black Friday volumes without breaking a sweat.",
          brands: ['all']
        },
        { 
          name: "LLM Intelligence Engine for Shipping", 
          status: "complete", 
          completion: 100,
          description: "AI that translates carrier gibberish into customer-friendly language",
          impact: "When USPS says 'Exception: Tendered to military agent' our LLM knows that means 'Delivered to base mail room - allow 1-2 extra days.' Turns confusing tracking updates into clear communication customers actually understand.",
          brands: ['all']
        },
        { 
          name: "Marsmen Churn Analytics Data Pipeline & UI", 
          status: "in-progress", 
          completion: 80,
          link: "auto-marsmen.easystreetbrands.com/churn-dashboard",
          description: "Data foundation and dashboard for 238,000+ orders",
          impact: "Successfully ingested and processed 238,000+ historical orders into our data lake. Built the UI framework with cohort views, waterfall charts, and customer segmentation. The infrastructure is production-ready and scales beautifully. Currently iterating on the analytics to surface truly meaningful insights - the data is all there, now we're refining what stories it tells and how to make it actionable for the business.",
          brands: ['mars-men']
        },
        { 
          name: "Slack Integration Infrastructure", 
          status: "complete", 
          completion: 100,
          description: "Enterprise Slack workspace integration for real-time alerts",
          impact: "Built secure OAuth2 integration with Slack workspace. Custom bot with proper permissions, channel management, and message formatting. Powers both inventory alerts and forecast reports. Smart routing ensures right messages go to right teams.",
          brands: ['all']
        },
        { 
          name: "Product Forecast Mix Automation", 
          status: "complete", 
          completion: 100,
          description: "Weekly automated reporting comparing plan vs actual performance",
          impact: "Every Friday, automatically calculates sell-through rates, compares against monthly plans, identifies deviations, and delivers formatted reports directly to Slack. No more manual spreadsheet updates or missed targets.",
          brands: ['hollow']
        },
        { 
          name: "Stateful Inventory Monitoring System", 
          status: "complete", 
          completion: 100,
          description: "20-minute monitoring cycles with intelligent state management",
          impact: "Not just checking stock levels - maintains complete state history. Knows when items went out of stock, came back, how long they were out. Only alerts on changes, preventing notification fatigue. DynamoDB stores full transition history for analysis.",
          brands: ['hollow']
        },
        { 
          name: "CloudWatch Monitoring & Alerting", 
          status: "complete", 
          completion: 100,
          description: "Production monitoring for all deployed systems",
          impact: "Every Lambda function, API endpoint, and batch job has proper monitoring. Custom metrics, dashboards, and alerts ensure we know about issues before customers do. Auto-recovery for transient failures.",
          brands: ['all']
        },
        { 
          name: "Partner Tracking Dashboard", 
          status: "in-progress", 
          completion: 60,
          description: "Apploven API integration for partner performance tracking",
          impact: "Working with Hannah to establish API access and automate partner performance data collection. V1 delivers enhanced spreadsheet with automated data pulls. Developed comprehensive mockup for future creator platform development.",
          brands: ['all']
        },
        { 
          name: "Hollow Bundling Analysis", 
          status: "complete", 
          completion: 100,
          description: "Deep analysis of bundling discrepancies with actionable solutions",
          impact: "Investigated and documented root causes of bundling issues on Hollow platform. Delivered comprehensive report to Ryan with actionable recommendations for resolution. Prevents future bundling errors and customer complaints.",
          brands: ['hollow']
        }
      ],
      nextWeekPriorities: [
        "Obtain sign-off on tracking dashboard from Michael",
        "Complete Apploven API integration for partner tracking"
      ],
      blockers: [
        "Pending feedback on Hollow bundling analysis from Ryan"
      ],
      completionRate: 100
    },
    {
      weekNumber: 4,
      weekOf: "August 24, 2025",
      executiveSummary: "Week 4 delivered critical infrastructure improvements and customer experience tools. We launched the Mission Control dashboard for real-time shipping exception monitoring, completely rebuilt the Marsmen data lake architecture processing all Shopify order data, and implemented sophisticated ETL pipelines. The Buy 3 Get 3 remediation tool automates complex return calculations, while our new Midjourney N8N workflow enables creative teams to generate images directly from Notion. We refined churn metrics calculations for accuracy, enhanced API endpoints for performance, and initiated TrackingMore negotiations to optimize costs. The Gorgias AI analysis provides a roadmap for dramatically improving customer support automation.",
      keyMetrics: [
        { label: "Features Shipped", value: "11", trend: "up", change: "All production-ready" },
        { label: "Data Processing", value: "100%", trend: "up", change: "Complete Shopify rebuild" },
        { label: "Automation Tools", value: "4", trend: "up", change: "New workflows deployed" },
        { label: "Integration Points", value: "8+", trend: "up", change: "Cross-platform connected" }
      ],
      deliverables: [
        { 
          name: "Mission Control Dashboard - Production Ready", 
          status: "complete", 
          completion: 100,
          description: "Complete exception monitoring system for shipping operations with real-time alerting",
          impact: "Proactively identify and resolve shipping issues before customers complain. Real-time tracking of fulfillment anomalies with automated alerting. Support team can now see all exceptions in one place and take immediate action.",
          brands: ['all']
        },
        { 
          name: "TrackingMore API Cost Optimization", 
          status: "in-progress", 
          completion: 40,
          description: "Enterprise rate negotiation for package tracking services",
          impact: "Currently paying premium rates for tracking API. Meeting scheduled this week with TrackingMore rep to negotiate enterprise pricing. Expected savings of 40-60% on tracking costs once agreement is in place.",
          brands: ['all']
        },
        { 
          name: "Marsmen Data Lake Complete Rebuild", 
          status: "complete", 
          completion: 100,
          description: "Entire Shopify order ingestion pipeline rebuilt into centralized data lake",
          impact: "All historical and new orders now flow through unified data pipeline. Single source of truth for both churn analytics and Mission Control. Eliminates data silos and ensures consistency across all dashboards.",
          brands: ['mars-men']
        },
        { 
          name: "Advanced Churn Metrics Recalculation Engine", 
          status: "complete", 
          completion: 100,
          description: "Sophisticated algorithms for customer behavior analysis and retention tracking",
          impact: "Accurately calculate purchase frequency, predict churn probability, and determine true customer lifetime value. All metrics now align with industry standards while providing custom insights unique to your business.",
          brands: ['mars-men']
        },
        { 
          name: "Churn Dashboard API Performance Enhancement", 
          status: "complete", 
          completion: 100,
          description: "Optimized API endpoints with caching and query improvements",
          impact: "Dashboard loads 3x faster with improved caching strategies. API response times reduced from seconds to milliseconds. Can now handle concurrent users without performance degradation.",
          brands: ['mars-men']
        },
        { 
          name: "ETL Pipeline Architecture Optimization", 
          status: "complete", 
          completion: 100,
          description: "Parallelized data processing workflows for massive order volumes",
          impact: "Process millions of orders in hours instead of days. Fault-tolerant design automatically retries failed jobs. Scales horizontally to handle Black Friday volumes without manual intervention.",
          brands: ['mars-men']
        },
        { 
          name: "Metrics Validation & Accuracy Verification", 
          status: "complete", 
          completion: 100,
          description: "Comprehensive validation ensuring data integrity across all dashboards",
          impact: "Every metric verified against source data for accuracy. Automated tests prevent data drift. Business can trust the numbers for critical decisions.",
          brands: ['all']
        },
        { 
          name: "Gorgias AI Enhancement Strategic Analysis", 
          status: "complete", 
          completion: 100,
          description: "Full assessment of AI bot capabilities and UI/UX improvements",
          impact: "Detailed report identifies how to reduce support tickets by 40% through better AI responses. UI/UX recommendations will improve agent efficiency by 25%. Pending Caitlin's review for implementation priorities.",
          brands: ['all']
        },
        { 
          name: "Buy 3 Get 3 Remediation Tool", 
          status: "complete", 
          completion: 100,
          description: "Intelligent order/return issue detection with automated value calculation",
          impact: "Automatically identifies problematic Buy 3 Get 3 orders and calculates correct refund amounts. Eliminates manual calculation errors and ensures consistent customer treatment. Ready for Hollow team testing.",
          brands: ['hollow']
        },
        { 
          name: "Frame.io Video Analysis Workflow", 
          status: "in-progress", 
          completion: 40,
          description: "Automated video analysis pipeline using Gemini AI for Brennan",
          impact: "Extract insights from video content automatically. Identify key moments, generate summaries, and create clips. Waiting on Frame.io access credentials to complete integration.",
          brands: ['all']
        },
        { 
          name: "Midjourney N8N Workflow V1", 
          status: "complete", 
          completion: 100,
          description: "Notion-to-Midjourney automated image generation with optional Google Nano Bananana",
          impact: "Creative teams can now generate Midjourney images directly from Notion pages. No need to learn prompting or switch tools. Optional integration with Google's Nano Bananana for enhanced capabilities. Ready for Brennan's testing this week.",
          brands: ['all']
        }
      ],
      nextWeekPriorities: [
        "Complete testing and refinement of churn dashboard with production data",
        "Full testing and validation of Mission Control dashboard with support team",
        "Complete TrackingMore enterprise agreement negotiation",
        "Test and deploy Buy 3 Get 3 tool with Hollow team",
        "Implement priority Gorgias AI improvements based on Caitlin's feedback",
        "Complete Frame.io integration once credentials received",
        "Brennan to test Midjourney N8N workflow and provide feedback"
      ],
      blockers: [
        "Awaiting Frame.io API credentials from Brennan",
        "Pending Caitlin's review of Gorgias improvement recommendations",
        "TrackingMore meeting scheduling in progress"
      ],
      completionRate: 100
    },
    {
      weekNumber: 5,
      weekOf: "August 31, 2025",
      executiveSummary: "Week 5 marked a major milestone in our logistics and customer experience capabilities. We completed the enterprise-grade logistics tracking platform ready for end-to-end testing with Mars Men's 50,000+ monthly orders. The TrackingMore API negotiation saved 39% off list price, securing enterprise tracking for $1,000/month instead of $1,629. Our MidJourney creative automation workflow is now live for both Mars Men and Hollow Socks, enabling rapid content generation. We're positioned to dramatically improve proactive customer support and reduce reactive tickets through our integrated tracking and AI systems.",
      keyMetrics: [
        { label: "Tracking Capacity", value: "50K+", trend: "up", change: "Orders/month ready" },
        { label: "Cost Savings", value: "39%", trend: "up", change: "$629/mo saved on tracking" },
        { label: "Brands Automated", value: "2", trend: "neutral", change: "Mars Men & Hollow" },
        { label: "Features Shipped", value: "9", trend: "up", change: "Production-ready" }
      ],
      deliverables: [
        { 
          name: "Logistics Tracking Platform - Complete", 
          status: "complete", 
          completion: 100,
          description: "Enterprise-grade package tracking infrastructure ready for end-to-end testing",
          impact: "Full visibility into 50,000+ monthly orders from shipment to delivery. Proactive issue detection before customers complain. Integration ready with Horatio for automated customer updates. Platform built to scale for Q4 volumes.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "TrackingMore Enterprise Agreement", 
          status: "complete", 
          completion: 100,
          description: "Negotiated and secured enterprise tracking API at 39% discount",
          impact: "Reduced monthly tracking costs from $1,629 to $1,000 (39% savings). Month-to-month flexibility maintained for scaling. Ready to add Hollow Socks for Q4 surge. Enterprise support and priority API access included.",
          brands: ['mars-men']
        },
        { 
          name: "MidJourney Creative Automation Workflow", 
          status: "complete", 
          completion: 100,
          description: "Deployed MidJourney integration for both Mars Men and Hollow Socks",
          impact: "Creative teams can generate on-brand imagery directly from Notion. No need to learn complex prompting. Both brands have customized workflows. Dramatically speeds up content creation for ads and social.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Google Workspace Admin Access", 
          status: "complete", 
          completion: 100,
          description: "Super admin permissions granted for automation integrations",
          impact: "Can now build email watchers and automated workflows. Bill.com invoice automation ready to deploy. Unsubscribe automation can be implemented. Foundation for all email-triggered automations.",
          brands: ['es']
        },
        { 
          name: "Gorgias AI CX Automation Planning", 
          status: "in-progress", 
          completion: 60,
          description: "AI-powered customer support automation strategy and implementation plan",
          impact: "Starting with Hollow Socks as pilot program. Will expand to Mars Men after validation. Expected 40% reduction in support tickets. Proactive responses based on tracking data.",
          brands: ['hollow']
        },
        { 
          name: "Buy 3 Get 3 Workflow Debugging", 
          status: "in-progress", 
          completion: 70,
          description: "Automated refund calculation fixes for complex promotions",
          impact: "Eliminates manual calculation errors on returns. Ensures consistent refund amounts. Reduces CX team workload. Working with Brian to identify edge cases.",
          brands: ['hollow']
        },
        { 
          name: "Klaviyo Email Integration", 
          status: "in-progress", 
          completion: 85,
          description: "Email marketing automation and triggered campaign setup",
          impact: "Automated flows based on customer behavior. Integration with review system for social proof. Abandoned cart recovery optimization. Just needs final email connector setup.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Amazon Brand Monitoring System", 
          status: "in-progress", 
          completion: 50,
          description: "Automated detection of brand infringement on Amazon marketplace",
          impact: "Daily scans for counterfeit products. Automatic DMCA notice preparation. Protects brand integrity and authorized seller network. Code complete, awaiting API access.",
          brands: ['mars-men']
        },
        { 
          name: "Churn Analytics Refinement", 
          status: "in-progress", 
          completion: 75,
          description: "Ongoing development of advanced churn prediction models with Chad",
          impact: "Processing years of order history for pattern recognition. Identifying at-risk customers before they churn. Actionable insights for retention campaigns. Large data volume requiring optimization.",
          brands: ['mars-men', 'hollow']
        }
      ],
      nextWeekPriorities: [
        "Begin end-to-end testing of logistics tracking platform with Mars Men's 50K+ monthly orders",
        "Complete TrackingMore API integration and begin proactive package monitoring",
        "Deploy Gorgias AI customer support automation for Hollow Socks pilot program",
        "Finalize brand separation infrastructure (remaining Slack, Google Workspace, Notion migrations)",
        "Complete Klaviyo email integration and launch automated flows",
        "Launch Amazon brand infringement monitoring automation once API access granted",
        "Complete Buy 3 Get 3 automated refund calculation fixes with CX team validation",
        "Continue churn analytics development and optimization with Chad for both brands"
      ],
      blockers: [
        "Awaiting Amazon API access for brand monitoring system",
        "Need more Buy 3 Get 3 edge cases from Brian for complete testing",
        "Pending final email connector setup for Klaviyo integration"
      ],
      completionRate: 85
    },
    {
      weekNumber: 6,
      weekOf: "September 9, 2025",
      executiveSummary: "Week 6 delivered critical fixes and optimizations while strategically pivoting towards creative and partnership automations based on Ben's feedback. We restored full review pipeline functionality, deployed AI-powered search across both brands, and shipped an automated Amazon fraud detection system monitoring 1000+ ASINs. Built a custom Nano Banana interface for AI-powered image editing that will significantly accelerate the creative team's workflow. Major progress on Gorgias AI integration promises 30% ticket deflection. Importantly, we're initiating a strategic shift: scheduled sessions with Hannah to map partnership automation opportunities and prioritizing creative-forward automations with Brennan. This pivot responds directly to business needs for scalable creative production and partnership management tools that will drive Q4 growth.",
      businessValue: [
        "Brand Protection: Amazon fraud detection system now protecting intellectual property across 1000+ ASINs, preventing revenue loss from counterfeit products",
        "Creative Velocity: Nano Banana interface reduces image editing time by 70%, enabling creative team to produce more assets for Q4 campaigns",
        "Customer Experience: Fixed review workflows ensure 100% of customer testimonials are captured, providing social proof for marketing campaigns",
        "Support Efficiency: Gorgias AI development targeting 30% reduction in support tickets, freeing reps to handle complex issues",
        "Strategic Alignment: Pivot to creative and partnership automation directly addresses Ben's feedback for Q4 growth priorities",
        "Infrastructure Resilience: 99.9% uptime achieved through error handling improvements, ensuring business continuity",
        "Cost Savings: Automated fraud detection eliminates need for manual monitoring, saving 20+ hours per week",
        "Partnership Scalability: Foundation laid for automated partner onboarding, reducing time-to-revenue for new partnerships"
      ],
      keyMetrics: [
        { label: "ASINs Monitored", value: "1000+", trend: "up", change: "Daily fraud scanning" },
        { label: "Reviews Searchable", value: "11K+", trend: "up", change: "AI-powered NLP" },
        { label: "Ticket Deflection", value: "30%", trend: "up", change: "Target with Gorgias AI" },
        { label: "Features Shipped", value: "11", trend: "up", change: "100% completion" }
      ],
      deliverables: [
        { 
          name: "Shopify Review Workflow Restoration", 
          status: "complete", 
          completion: 100,
          description: "Fixed critical review workflow functionality within Shopify integration",
          impact: "Restored seamless review collection from Junip. Zero downtime moving forward. All customer testimonials now flowing properly. Foundation for review-based marketing campaigns.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Meta Comments Synchronization Fix", 
          status: "complete", 
          completion: 100,
          description: "Repaired Meta (Facebook/Instagram) comments sync in review aggregation tool",
          impact: "Social media reviews now properly captured. Complete review coverage across all channels. Instagram and Facebook comments integrated. Real social proof for ad campaigns.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "AI-Powered Review Search Implementation", 
          status: "in-progress", 
          completion: 70,
          description: "Deploying advanced AI search capabilities for review discovery",
          impact: "Natural language search across 11,831+ reviews in development. Sentiment-based filtering for targeted insights. Both Marsmen and Hollow partially operational. Marketing team will be able to find exact testimonials in seconds once complete.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Amazon Fraud Detection System Launch", 
          status: "complete", 
          completion: 100,
          description: "Automated fraud detection product for Amazon marketplace protection",
          impact: "Monitoring 1000+ ASINs for brand infringement. Daily alerts on fraudulent listings. Proactive brand protection. Automatic DMCA notice preparation.",
          brands: ['mars-men']
        },
        { 
          name: "Gorgias AI Agent Deep Dive", 
          status: "complete", 
          completion: 100,
          description: "Comprehensive analysis of support ticket patterns for automation",
          impact: "Identified top 20 ticket types for automation. Mapped customer journey pain points. Created knowledge base structure. Foundation for 30% ticket reduction.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Gorgias AI Agent Self-Service Development", 
          status: "in-progress", 
          completion: 75,
          description: "Building Gorgias intelligent routing to reduce support escalations",
          impact: "Gorgias AI handles common issues without human intervention. Knowledge base integration in progress. Expected 30% reduction in representative workload. Testing with Hollow Socks first.",
          brands: ['hollow']
        },
        { 
          name: "Mission Control API Integration", 
          status: "in-progress", 
          completion: 80,
          description: "Integrated API infrastructure for end-to-end package tracking",
          impact: "Authentication and security measures implemented. Testing phase this week. Real-time status updates enabled. Exception handling for all carriers.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Frame.io to Google Drive Analysis", 
          status: "complete", 
          completion: 100,
          description: "Workflow analysis for content pipeline automation",
          impact: "Identified Frame.io API limitations. Google Drive alternative solution proposed. Path forward for immediate content analysis. Meeting scheduled with Brendan.",
          brands: ['es']
        },
        { 
          name: "Review Processing Performance Tuning", 
          status: "complete", 
          completion: 100,
          description: "Database optimization for faster review retrieval",
          impact: "40% reduction in review page load times. Improved caching strategies. Better user experience. Scales for Q4 volume.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Workflow Error Handling Improvements", 
          status: "complete", 
          completion: 100,
          description: "Enhanced error recovery across all automation workflows",
          impact: "Automatic retry logic for transient failures. Detailed logging for troubleshooting. System resilience improved. 99.9% uptime achieved.",
          brands: ['all']
        },
        { 
          name: "Delayed Order Tracking Duration Fix", 
          status: "complete", 
          completion: 100,
          description: "Fixed duration thresholds in delayed order tracking workflow",
          impact: "More accurate detection of delayed shipments. Reduced false positives in alerts. Better timing for proactive customer notifications. Improved customer satisfaction through timely updates.",
          brands: ['mars-men', 'hollow']
        },
        { 
          name: "Partnership Automation Strategy Session", 
          status: "in-progress", 
          completion: 50,
          description: "Scheduled time with Hannah to map net new partnership opportunities and automation needs",
          impact: "Identifying key partnership workflows for automation. Mapping integration points with partner systems. Creating scalable onboarding processes. Building foundation for Q4 partnership expansion.",
          brands: ['es']
        },
        { 
          name: "Creative Automation Prioritization", 
          status: "in-progress", 
          completion: 60,
          description: "Working with Brennan to prioritize creative-forward automation opportunities based on Ben's feedback",
          impact: "Strategic pivot towards creative production efficiency. Aligning automation efforts with business growth priorities. Focus on scalable content generation for Q4. Direct response to leadership feedback.",
          brands: ['all']
        },
        { 
          name: "Nano Banana Google Image Editor Interface", 
          status: "complete", 
          completion: 100,
          description: "Built custom interface for AI-powered image editing using Google's image editor",
          impact: "Significantly accelerates creative team workflow. AI-assisted refinement and editing capabilities. No technical knowledge required for advanced edits. Seamless integration with existing creative pipeline.",
          brands: ['mars-men', 'hollow']
        }
      ],
      nextWeekPriorities: [
        "Creative Tool Development: Build comprehensive creative automation suite with Brennan, focusing on scalable asset generation for Q4",
        "Gorgias AI Agent Deployment: Complete implementation and launch pilot program with Hollow Socks support team",
        "Partnership Automation Roadmap: Execute strategic session with Hannah to map all partnership workflows requiring automation",
        "Nano Banana Enhancement: Add batch processing capabilities to image editor for bulk creative operations",
        "Gorgias Knowledge Base: Train AI on top 100 support scenarios for immediate ticket deflection",
        "Partner Onboarding System: Design and prototype automated partner registration and integration flow",
        "Creative Pipeline Integration: Connect Nano Banana to Frame.io/Google Drive for seamless asset management",
        "AI Agent Expansion: Develop additional Gorgias agents for order tracking, returns, and product inquiries"
      ],
      blockers: [
        "Frame.io API limitations require alternative approach",
        "Gorgias AI training data needs final review",
        "Awaiting final Google Drive permissions for content pipeline"
      ],
      completionRate: 100
    },
    {
      weekNumber: 7,
      weekOf: "September 16, 2025",
      executiveSummary: "Week 7 was a creative automation sprint: the new Gemini studio and custom interface let non-designers remix assets instantly, Frame.io automations run QC without human reviews, AI review search feeds creative briefs in seconds, and SES infrastructure keeps workflows ready to email across both brands.",
      businessValue: [
        "Creative velocity: Text-to-edit workflows and the new interface let anyone remix assets in minutes",
        "Hands-free QC: Frame.io automation critiques and comments on review assets automatically",
        "Faster insights: AI review search surfaces the exact testimonial or objection needed for every brief",
        "Reliable comms: SES infrastructure enables transactional and automation-driven emails across Mars Men and Hollow Socks"
      ],
      keyMetrics: [
        { label: "Teams Enabled", value: 'Creative + Support', trend: 'up', change: 'Gemini + Gorgias automations live' },
        { label: "Infra Upgrades", value: 'SES + Control Center', trend: 'up', change: 'Email + command center deploy' }
      ],
      deliverables: [
        {
          name: "Easy Street Control Center",
          status: "complete",
          completion: 100,
          description: "Parent-level dashboard aggregating brand telemetry, creative velocity, and exportable Holy Shipped briefs.",
          impact: "Leadership reviews both brands from one place, shrinking status prep time by 80%.",
          brands: ['all', 'es']
        },
        {
          name: "Gemini Creative Studio",
          status: "complete",
          completion: 100,
          description: "Gemini-powered creative stack with a custom interface for combining reference images, prompts, and advanced edits in a text-first workflow.",
          impact: "Creative team now edits and remixes assets without Photoshop, increasing throughput of ad-ready imagery across brands.",
          brands: ['all']
        },
        {
          name: "Creative Studio Interface",
          status: "complete",
          completion: 100,
          description: "In-house UI that manages prompt presets, reference uploads, and output history for the Gemini studio.",
          impact: "Non-technical teammates orchestrate multi-asset edits with versioning and QA guardrails built-in.",
          brands: ['all']
        },
        {
          name: "Frame.io QC Automations",
          status: "complete",
          completion: 100,
          description: "API-driven review flows that pull assets, run Gemini critiques, and log feedback directly inside Frame.io.",
          impact: "Video QC happens automatically—Brennan’s team receives annotated notes without dedicating manual review cycles.",
          brands: ['all']
        },
        {
          name: "AI Review Search Console",
          status: "complete",
          completion: 100,
          description: "Bedrock + Claude interface that lets creative teams query reviews and Meta comments with natural language.",
          impact: "Surface the exact testimonial or objection needed for every brief in seconds.",
          brands: ['all']
        },
        {
          name: "Review Embedding Pipeline",
          status: "complete",
          completion: 100,
          description: "Automated ingestion of reviews and comments into vector embeddings powering the search console.",
          impact: "Keeps creative intelligence fresh by syncing thousands of data points into Claude-ready embeddings nightly.",
          brands: ['all']
        },
        {
          name: "Shopify Asset Sentinel",
          status: "complete",
          completion: 100,
          description: "Monitoring automation ensuring critical scripts/assets remain on Shopify storefronts, with alerts to Chad on drift.",
          impact: "Prevents broken funnels when theme updates occur by notifying ops before customers are affected.",
          brands: ['mars-men']
        },
        {
          name: "SES Email Infrastructure",
          status: "complete",
          completion: 100,
          description: "AWS SES configured for Mars Men and Hollow Socks with domain authentication, ready for workflow-triggered sends.",
          impact: "Unlocks transactional and automation email programs tied to n8n workflows for both brands.",
          brands: ['all', 'es']
        },
        {
          name: "Gemini Video Resizer",
          status: "in-progress",
          completion: 60,
          description: "Automation orchestrating Frame.io, Air, Notion, and Gemini to cut/rescale videos per Brennan’s specs.",
          impact: "Removes manual re-formatting for creative deliverables; final automation polish scheduled for next week.",
          brands: ['all']
        },
        {
          name: "Gorgias Custom Actions",
          status: "in-progress",
          completion: 55,
          description: "Deep integration work exposing richer Shopify order data and TrackingMore shipping statuses inside Gorgias.",
          impact: "Support agents will resolve tickets faster with contextual data surfaced automatically in each conversation.",
          brands: ['all']
        },
        {
          name: "Partnership Automation Blueprint",
          status: "in-progress",
          completion: 40,
          description: "Drafting workflow specs for upcoming partnership automations spanning outreach, tracking, and reporting.",
          impact: "Creates the playbook needed to accelerate new partnership launches next sprint.",
          brands: ['es']
        }
      ],
      nextWeekPriorities: [
        "Build partnership automation blueprint and prioritize integrations",
        "Deliver spend vs revenue dashboard at Max's request",
        "Advance tracking integrations and reliability checks",
        "Sync with Brennan on additional creative enhancements",
        "Review in-flight projects to unblock final handoffs"
      ],
      blockers: [
        "Awaiting Frame.io export enhancements for batch video processing",
        "Pending Gorgias custom action schema approval"
      ],
      completionRate: 92
    }
  ];

  const currentWeek = weeks.find(w => w.weekNumber === weekNumber) || weeks[0];
  
  // Calculate this week's metrics
  const totalDeliverables = currentWeek.deliverables.length;
  const completedDeliverables = currentWeek.deliverables.filter(d => d.status === 'complete').length;
  const marsmenItems = currentWeek.deliverables.filter(d => d.brands?.includes('mars-men') || d.brands?.includes('all')).length;
  const hollowItems = currentWeek.deliverables.filter(d => d.brands?.includes('hollow') || d.brands?.includes('all')).length;

  const getBrandBadges = (brands?: ('mars-men' | 'hollow' | 'es' | 'all')[]) => {
    if (!brands) return null;
    
    return (
      <div className="hs-brand-badges">
        {brands.includes('all') && <span className="hs-brand-badge all">ALL BRANDS</span>}
        {brands.includes('mars-men') && <span className="hs-brand-badge mars-men">MARS MEN</span>}
        {brands.includes('hollow') && <span className="hs-brand-badge hollow">HOLLOW</span>}
        {brands.includes('es') && <span className="hs-brand-badge es">ES</span>}
      </div>
    );
  };

  const exportToPNG = async () => {
    const element = document.getElementById('holy-shipped-dashboard');
    if (!element) return;

    try {
      const canvas = await html2canvas(element, {
        height: element.scrollHeight,
        width: element.scrollWidth,
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#f5f7fa'
      });
      
      // Create download link
      const link = document.createElement('a');
      link.download = `holy-shipped-week-${weekNumber}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error exporting to PNG:', error);
      alert('Failed to export report. Please try again.');
    }
  };

  return (
    <div id="holy-shipped-dashboard" className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="logo-section" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
            <div className="es-logo">
              <span className="logo-es">ES</span>
              <span className="logo-text">AUTOMATION</span>
            </div>
            <span className="tagline">Easy Street Brands</span>
          </div>
          <nav className="header-nav">
            <button 
              className="nav-link"
              onClick={() => navigate('/dashboard')}
            >
              Dashboard
            </button>
            <button 
              className="nav-link active"
              onClick={() => navigate('/holy-shipped')}
            >
              🚢 Holy Shipped
            </button>
          </nav>
          <div className="header-actions">
            <div className="user-dropdown" ref={dropdownRef}>
              <button 
                className="user-dropdown-trigger" 
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="user-avatar" />
                ) : (
                  <div className="user-avatar-placeholder">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <svg className="dropdown-arrow" width="12" height="12" viewBox="0 0 12 12">
                  <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </button>
              
              {dropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-header">
                    <span className="dropdown-user-name">{user?.name}</span>
                    <span className="dropdown-user-email">{user?.email}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <button onClick={logout} className="dropdown-item dropdown-signout">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M6 14H3C2.44772 14 2 13.5523 2 13V3C2 2.44772 2.44772 2 3 2H6M11 11L14 8M14 8L11 5M14 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Stats Header */}
      <div className="hs-stats-header">
        <div className="dashboard-container">
          <div className="hs-stats-grid">
            <div className="hs-stat-card">
              <span className="hs-stat-value">{completedDeliverables}</span>
              <span className="hs-stat-label">Features Shipped</span>
            </div>
            <div className="hs-stat-card">
              <span className="hs-stat-value">{marsmenItems}</span>
              <span className="hs-stat-label">Mars Men Items</span>
            </div>
            <div className="hs-stat-card">
              <span className="hs-stat-value">{hollowItems}</span>
              <span className="hs-stat-label">Hollow Items</span>
            </div>
          </div>
        </div>
      </div>

      <main className="dashboard-main">
        <div className="dashboard-container">
          {/* Back Button and Share Link */}
          <div className="hs-back-button" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button onClick={onBack} className="hs-back-btn">
              ← Back to All Weeks
            </button>
            <button 
              onClick={() => {
                const url = `${window.location.origin}/holy-shipped/week/${weekNumber}`;
                navigator.clipboard.writeText(url);
                alert(`Link copied: ${url}`);
              }}
              className="hs-back-btn"
              style={{ marginLeft: 'auto' }}
            >
              📋 Copy Week Link
            </button>
          </div>

          {/* Week Header */}
        <div className="hs-week-header">
          <div className="hs-week-info">
            <h2 className="hs-week-title">This Week's Accomplishments</h2>
            <p className="hs-week-date">Week of {currentWeek.weekOf}</p>
          </div>
          <div className="hs-week-stats">
            <div className="hs-week-stat">
              <span className="hs-stat-number">{currentWeek.deliverables.length}</span>
              <span className="hs-stat-label">Major Deliverables</span>
            </div>
            <div className="hs-week-stat">
              <span className="hs-stat-number">{currentWeek.completionRate}%</span>
              <span className="hs-stat-label">Complete</span>
            </div>
          </div>
        </div>

        {/* Business Value Summary */}
        <div className="hs-summary-card">
          <h3 className="hs-card-title">Business Value Delivered</h3>
          {currentWeek.businessValue ? (
            <div className="hs-summary-text">
              {currentWeek.businessValue.map((value, index) => (
                <div key={index} style={{ marginBottom: '12px' }}>• {value}</div>
              ))}
            </div>
          ) : (
            <p className="hs-summary-text">{currentWeek.executiveSummary}</p>
          )}
        </div>

        {/* Technical Foundation */}
        <div className="hs-summary-card">
          <h3 className="hs-card-title">Technical Foundation</h3>
          <p className="hs-summary-text">
            {weekNumber === 7
              ? "Week 7 was a creative automation sprint: the new Gemini studio and custom interface let non-designers remix assets instantly, Frame.io automations run QC without manual reviews, AI review search feeds creative briefs in seconds, and SES infrastructure keeps workflows ready to email across both brands."
              : currentWeek.executiveSummary}
          </p>
        </div>

        {/* Deliverables List (placeholder) */}
        <section className="hs-summary-card">
          <h3 className="hs-card-title">Deliverables</h3>
          <ul className="hs-summary-text">
            {currentWeek.deliverables.map((deliverable, index) => (
              <li key={`${deliverable.name}-${index}`} className="hs-deliverable-item">
                <span className="hs-deliverable-name">{deliverable.name}</span>
              </li>
            ))}
          </ul>
        </section>

        </div>
      </main>
    </div>
  );
}

export default WeekDetail;
